import React from "react";
import { includes, without } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import FilterList from "@radarly/components/filters/list";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import {
  Categories,
  FormatedFacet,
  FullFacet,
  categories,
} from "@radarly/models/facets";

type FilterCategories = Record<keyof typeof Categories, Facet[]>;

interface IProps {
  openFieldPanel: (field: Facet) => void;
  usedFields: string[];
  showQueries?: boolean;
  customCategories?: Facet[];
  hiddenEmptyFacets?: Facet[];
  excludedFacets?: Facet[];
  hideCustomFields?: boolean;
}

const formatCustomCategories = (
  customCategories: Facet[]
): FilterCategories => {
  //object need to be copied, otherwise the splice mutation will mess with the final result
  const _customCategories = [...customCategories];
  const cleanFacets = (facets: Facet[]) => {
    const validFacets = [];
    facets.forEach((facet) => {
      const facetIndex = _customCategories.indexOf(facet);
      if (facetIndex >= 0) {
        _customCategories.splice(facetIndex, 1);
        validFacets.push(facet);
      }
    });
    return validFacets;
  };

  const finalCategories = Object.entries(categories).map(
    ([category, facets]) => [category, cleanFacets(facets)]
  );
  const finalCategoriesObj = Object.fromEntries(finalCategories);
  finalCategoriesObj["misc"].push(..._customCategories);
  return finalCategoriesObj;
};

// Don't show "Queries" in ProjectIP filters https://linkfluence.atlassian.net/browse/SUPPORTLKF-164
export const getCategories = (
  showQueries: boolean,
  customCategories?: Facet[]
): FilterCategories => {
  if (customCategories) {
    return formatCustomCategories(customCategories);
  }
  if (showQueries) {
    return categories;
  }
  return {
    ...categories,
    basic: without(categories.basic, Facet.Focuses),
  };
};

export const hideFacetWhenEmpty = (facetName: FullFacet["id"]) =>
  includes(
    [
      Facet.Corpus,
      Facet.ImageRecognitionLogos,
      Facet.ImageRecognitionPersonIdentifications,
      Facet.InfluencersTags,
    ],
    facetName
  );

function getValidFacets(
  facets: Record<string, FormatedFacet>,
  excludedFacets: Facet[],
  hideCustomFields: boolean
): Record<string, FormatedFacet> {
  const validFacets = Object.entries(facets).filter(([_, data]) => {
    if (hideCustomFields) {
      return !data.custom && !excludedFacets.includes(data.id as Facet);
    }
    return !excludedFacets.includes(data.id as Facet);
  });
  return Object.fromEntries(validFacets);
}

const RadarlySharedFilterbarPanelsNewFilters = ({
  openFieldPanel,
  usedFields,
  showQueries = true,
  customCategories,
  hiddenEmptyFacets = [],
  excludedFacets = [],
  hideCustomFields = false,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const allFacets = useFacetsFromContext();
  const displayedCategories = getCategories(showQueries, customCategories);
  const allValidFacets = getValidFacets(
    allFacets,
    excludedFacets,
    hideCustomFields
  );
  return (
    <FilterList
      onClick={openFieldPanel}
      facets={allValidFacets}
      locale={locale}
      categories={displayedCategories}
      usedFacets={usedFields}
      hiddenEmptyFacets={hiddenEmptyFacets}
    />
  );
};

export default RadarlySharedFilterbarPanelsNewFilters;
